
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { useGame } from '../contexts/GameContext';
import { TrendingUp, TrendingDown, ChartBar } from "lucide-react";

const PerformanceMetrics: React.FC = () => {
  const { kpis } = useGame();
  const [prevKpis, setPrevKpis] = useState(kpis);
  const [highlightedKpis, setHighlightedKpis] = useState<Record<string, boolean>>({});
  
  // Track KPI changes for animations
  useEffect(() => {
    const changedKpis: Record<string, boolean> = {};
    
    Object.keys(kpis).forEach((key) => {
      if (kpis[key as keyof typeof kpis] !== prevKpis[key as keyof typeof kpis]) {
        changedKpis[key] = true;
      }
    });
    
    if (Object.keys(changedKpis).length > 0) {
      setHighlightedKpis(changedKpis);
      
      // Clear the highlights after animation
      const timer = setTimeout(() => {
        setHighlightedKpis({});
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    setPrevKpis(kpis);
  }, [kpis, prevKpis]);
  
  // Helper function for trending indicators
  const TrendIndicator: React.FC<{ value: number, target: number, higherIsBetter: boolean }> = ({ 
    value, target, higherIsBetter 
  }) => {
    const isGood = higherIsBetter ? value > target : value < target;
    
    return (
      <div className={`flex items-center ${isGood ? 'text-bitcoin-accent' : 'text-destructive'}`}>
        {isGood ? (
          <TrendingUp className="h-4 w-4 mr-1" />
        ) : (
          <TrendingDown className="h-4 w-4 mr-1" />
        )}
        <span className="text-xs">{isGood ? 'Good' : 'Needs Improvement'}</span>
      </div>
    );
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Uptime Card */}
      <Card className={`bg-web3-blue/10 border-web3-blue/20 h-full transition-all duration-500 ${
        highlightedKpis['uptime'] ? 'ring-2 ring-web3-green shadow-lg scale-105' : ''
      }`}>
        <CardContent className="pt-6 h-full flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="w-full">
              <p className="stat-label text-white/70">UPTIME</p>
              <p className={`stat-value text-white text-2xl font-bold ${
                highlightedKpis['uptime'] ? 'animate-pulse' : ''
              }`}>
                {kpis.uptime.toFixed(1)}%
              </p>
              <div className="mt-2">
                <TrendIndicator value={kpis.uptime} target={95} higherIsBetter={true} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-web3-green/20 flex items-center justify-center">
              <ChartBar className="h-5 w-5 text-web3-green" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Energy Cost Card */}
      <Card className={`bg-web3-blue/10 border-web3-blue/20 h-full transition-all duration-500 ${
        highlightedKpis['energyCost'] ? 'ring-2 ring-web3-green shadow-lg scale-105' : ''
      }`}>
        <CardContent className="pt-6 h-full flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="w-full">
              <p className="stat-label text-white/70">ENERGY COST</p>
              <p className={`stat-value text-white text-2xl font-bold ${
                highlightedKpis['energyCost'] ? 'animate-pulse' : ''
              }`}>
                ${kpis.energyCost.toFixed(3)}/kWh
              </p>
              <div className="mt-2">
                <TrendIndicator value={kpis.energyCost} target={0.06} higherIsBetter={false} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-web3-green/20 flex items-center justify-center">
              <ChartBar className="h-5 w-5 text-web3-green" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Profit Margin Card */}
      <Card className={`bg-web3-blue/10 border-web3-blue/20 h-full transition-all duration-500 ${
        highlightedKpis['profitMargin'] ? 'ring-2 ring-web3-green shadow-lg scale-105' : ''
      }`}>
        <CardContent className="pt-6 h-full flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="w-full">
              <p className="stat-label text-white/70">PROFIT MARGIN</p>
              <p className={`stat-value text-white text-2xl font-bold ${
                highlightedKpis['profitMargin'] ? 'animate-pulse' : ''
              }`}>
                {kpis.profitMargin.toFixed(1)}%
              </p>
              <div className="mt-2">
                <TrendIndicator value={kpis.profitMargin} target={20} higherIsBetter={true} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-web3-green/20 flex items-center justify-center">
              <ChartBar className="h-5 w-5 text-web3-green" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Efficiency Card */}
      <Card className={`bg-web3-blue/10 border-web3-blue/20 h-full transition-all duration-500 ${
        highlightedKpis['efficiency'] ? 'ring-2 ring-web3-green shadow-lg scale-105' : ''
      }`}>
        <CardContent className="pt-6 h-full flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="w-full">
              <p className="stat-label text-white/70">EFFICIENCY</p>
              <p className={`stat-value text-white text-2xl font-bold ${
                highlightedKpis['efficiency'] ? 'animate-pulse' : ''
              }`}>
                {kpis.efficiency.toFixed(1)}%
              </p>
              <div className="mt-2">
                <TrendIndicator value={kpis.efficiency} target={80} higherIsBetter={true} />
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-web3-green/20 flex items-center justify-center">
              <ChartBar className="h-5 w-5 text-web3-green" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceMetrics;
